import { Capacitor } from '@capacitor/core';
import { Geolocation as CapGeolocation } from '@capacitor/geolocation';
import { registerPlugin } from '@capacitor/core';

/**
 * Native-first GPS adapter for Brema ValliRider.
 *
 * On native platforms (iOS/Android via Capacitor):
 *   - Uses @capacitor-community/background-geolocation for continuous
 *     tracking that survives the WebView going idle or the screen
 *     locking. On Android this runs as a foreground service; on iOS
 *     it uses the CoreLocation "Always" authorization.
 *   - Callback receives { latitude, longitude, accuracy, altitude,
 *     speed (m/s), bearing (deg), time (ms) } — mirroring the browser
 *     GeolocationPosition.coords contract.
 *
 * On the web (PWA / dev preview):
 *   - Falls back to navigator.geolocation.watchPosition — same
 *     behaviour the app has always had, so nothing regresses.
 *
 * The returned watcher has a stable shape so useTracking.js does not
 * need to care which backend produced the fix.
 */

// Lazy plugin handle — registerPlugin is a no-op on web that returns
// a proxy which routes to the web implementation. On native it binds
// to the compiled ObjC/Java code.
const BackgroundGeolocationPlugin = registerPlugin('BackgroundGeolocation');

const isNative = () => Capacitor.isNativePlatform();

/**
 * Request the correct permissions up front. On native this triggers
 * the OS prompt (foreground + background). On web it's a no-op —
 * the browser prompts on first watchPosition call.
 */
export async function requestLocationPermission() {
  if (!isNative()) return { granted: true, background: true };
  try {
    // Capacitor Geolocation exposes requestPermissions which returns
    // { location: 'granted'|'denied'|'prompt', coarseLocation: ... }
    const res = await CapGeolocation.requestPermissions({
      permissions: ['location', 'coarseLocation'],
    });
    return {
      granted: res.location === 'granted',
      background: res.location === 'granted',
      raw: res,
    };
  } catch (e) {
    return { granted: false, background: false, error: e?.message || String(e) };
  }
}

/**
 * Start watching the device location.
 *
 * @param {(fix: NormalizedFix) => void} onFix   Called on every update.
 * @param {(err: Error) => void}          onError Called on failure.
 * @param {object} opts
 * @param {number} [opts.distanceFilterM=1]  Minimum meters between fixes.
 * @param {string} [opts.backgroundTitle]    Foreground service title (Android).
 * @param {string} [opts.backgroundMessage]  Foreground service body (Android).
 * @returns {Promise<{ stop: () => Promise<void>, backend: 'native'|'web' }>}
 *
 * NormalizedFix: {
 *   latitude: number, longitude: number,
 *   accuracy: number|null,
 *   speed: number|null,       // m/s (null when unknown)
 *   heading: number|null,     // degrees 0..360 (null when unknown)
 *   altitude: number|null,
 *   time: number              // ms since epoch
 * }
 */
export async function startWatch(onFix, onError, opts = {}) {
  const distanceFilterM = opts.distanceFilterM ?? 1;

  if (isNative()) {
    // ---- NATIVE PATH: background-geolocation ----
    const watcherId = await BackgroundGeolocationPlugin.addWatcher(
      {
        backgroundTitle: opts.backgroundTitle || 'Brema ValliRider',
        backgroundMessage:
          opts.backgroundMessage ||
          'Tracciamento GPS attivo — mantieni l\'app aperta per la navigazione.',
        requestPermissions: true,
        stale: false,
        distanceFilter: distanceFilterM,
      },
      (location, error) => {
        if (error) {
          // Distinguish PERMANENT vs TRANSIENT native errors:
          // - NOT_AUTHORIZED / permission denied → permanent
          // - anything else (POSITION_UNAVAILABLE, TIMEOUT, weak signal) →
          //   transient; keep the watcher alive.
          if (error.code === 'NOT_AUTHORIZED') {
            onError?.(Object.assign(new Error('LOCATION_PERMISSION_DENIED'), { code: 'PERMISSION_DENIED', fatal: true }));
            return;
          }
          onError?.(Object.assign(new Error(error.message || String(error)), { code: 'TRANSIENT', fatal: false }));
          return;
        }
        if (!location) return;
        onFix?.({
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy ?? null,
          speed: location.speed ?? null,
          heading:
            location.bearing != null && !Number.isNaN(location.bearing)
              ? location.bearing
              : null,
          altitude: location.altitude ?? null,
          time: location.time ?? Date.now(),
        });
      }
    );

    return {
      backend: 'native',
      stop: async () => {
        try {
          await BackgroundGeolocationPlugin.removeWatcher({ id: watcherId });
        } catch { /* silent */ }
      },
    };
  }

  // ---- WEB FALLBACK: navigator.geolocation.watchPosition ----
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    onError?.(new Error('GPS_UNAVAILABLE'));
    return { backend: 'web', stop: async () => {} };
  }

  const wId = navigator.geolocation.watchPosition(
    (pos) => {
      onFix?.({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy ?? null,
        speed: pos.coords.speed ?? null,
        heading:
          pos.coords.heading != null && !Number.isNaN(pos.coords.heading)
            ? pos.coords.heading
            : null,
        altitude: pos.coords.altitude ?? null,
        time: pos.timestamp || Date.now(),
      });
    },
    (err) => {
      // W3C Geolocation error codes:
      //   1 = PERMISSION_DENIED   (permanent)
      //   2 = POSITION_UNAVAILABLE (transient — tunnel, weak signal)
      //   3 = TIMEOUT              (transient — will retry automatically)
      // Only PERMISSION_DENIED should tear down tracking. Anything else
      // is a hiccup and the watcher keeps polling in the background.
      const code = err?.code === 1 ? 'PERMISSION_DENIED' : 'TRANSIENT';
      const fatal = code === 'PERMISSION_DENIED';
      const wrapped = new Error(err?.message || 'GPS error');
      wrapped.code = code;
      wrapped.fatal = fatal;
      onError?.(wrapped);
    },
    { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
  );

  return {
    backend: 'web',
    stop: async () => {
      try {
        navigator.geolocation.clearWatch(wId);
      } catch { /* silent */ }
    },
  };
}

export function isNativePlatform() {
  return isNative();
}
